import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import api from '../services/api'
import Reveal from '../components/landing/Reveal'
import CinematicNav from '../components/landing/CinematicNav'
import CinematicFooter from '../components/landing/CinematicFooter'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  ArrowRight,
  Clock,
  Users,
  Target,
  Layers,
  Camera,
  Film,
  Palette,
  Sparkles,
  Monitor,
  Zap,
} from 'lucide-react'

const DISCIPLINE_ICONS = {
  'Photography': Camera,
  'Video': Film,
  'Design': Palette,
  'Animation': Sparkles,
  'UI/UX': Monitor,
  'AI / Creative': Zap,
}

export default function ProjectLabPage() {
  const [featured, setFeatured] = useState(null)
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const load = async () => {
      try {
        const [featuredRes, allRes] = await Promise.all([
          api.get('/project-lab/projects/featured').catch(() => ({ data: {} })),
          api.get('/project-lab/projects').catch(() => ({ data: { projects: [] } })),
        ])
        setFeatured(featuredRes.data?.project || null)
        // Exclude the featured project from the catalogue list
        const featuredId = featuredRes.data?.project?.id
        setProjects(
          (allRes.data?.projects || []).filter(p => p.id !== featuredId)
        )
      } catch (err) {
        console.error('[ProjectLab] Load error:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>Project Lab — Learn by Creating | Synergy Multimedia Academy</title>
        <meta
          name="description"
          content="SMA Project Lab: professional projects with expert mentorship. Photography, video, design, animation, UI/UX, and AI. Real projects, real outcomes, portfolio-ready work."
        />
        <link rel="canonical" href="https://synergymultimediaacademy.com/project-lab" />
        <meta property="og:title" content="Project Lab — Learn by Creating | SMA" />
        <meta
          property="og:description"
          content="Professional projects with expert mentorship. Produce portfolio-ready work in photography, video, design, and more."
        />
        <meta property="og:url" content="https://synergymultimediaacademy.com/project-lab" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="cinema-grain" aria-hidden="true" />

      {/* Official SMA Public Navigation */}
      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} onNavigate={() => {}} />

      {/* Hero Section */}
      <header className="relative pt-28 pb-12 sm:pt-36 sm:pb-20 overflow-hidden border-b border-white/5">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10">
          <Reveal className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cinema-gold/10 border border-cinema-gold/30 text-cinema-gold text-xs uppercase tracking-[0.16em] font-sans font-semibold mb-4 sm:mb-6 rounded-none">
              <Target className="w-3.5 h-3.5" />
              <span>Project Lab</span>
            </div>

            <h1 className="cinema-headline-xl text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-white font-light tracking-tight">
              LEARN BY <span className="text-cinema-gold">CREATING</span>.
            </h1>

            <p className="mt-3 sm:mt-6 text-xs sm:text-sm md:text-base text-cinema-soft/80 leading-relaxed max-w-2xl font-sans">
              Professional projects with expert mentorship. Work through a defined
              production workflow, make real decisions, and deliver portfolio-ready
              professional work.
            </p>

            <p className="mt-3 text-xs text-cinema-muted font-sans leading-relaxed max-w-xl">
              Courses teach skills. Project Lab turns skills into professional outcomes.
            </p>
          </Reveal>

          {/* Cardless Horizontal Metrics Strip — One Coherent Information Band */}
          <Reveal delay={100} className="mt-10 sm:mt-16 border-y border-white/10">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
              <div className="py-6 sm:py-7 px-4 sm:px-6 lg:px-8 first:pl-0">
                <p className="text-[0.65rem] sm:text-xs font-mono uppercase tracking-[0.2em] text-cinema-gold font-semibold mb-2">
                  01 / Approach
                </p>
                <p className="font-display text-xl sm:text-2xl lg:text-3xl text-white font-light tracking-tight">
                  Real Projects
                </p>
                <p className="text-xs text-cinema-muted font-sans mt-1">
                  Professional commercial outcomes
                </p>
              </div>

              <div className="py-6 sm:py-7 px-4 sm:px-6 lg:px-8">
                <p className="text-[0.65rem] sm:text-xs font-mono uppercase tracking-[0.2em] text-cinema-gold font-semibold mb-2">
                  02 / Cohort
                </p>
                <p className="font-display text-xl sm:text-2xl lg:text-3xl text-white font-light tracking-tight">
                  5–6 People
                </p>
                <p className="text-xs text-cinema-muted font-sans mt-1">
                  Intimate group mentorship
                </p>
              </div>

              <div className="py-6 sm:py-7 px-4 sm:px-6 lg:px-8">
                <p className="text-[0.65rem] sm:text-xs font-mono uppercase tracking-[0.2em] text-cinema-gold font-semibold mb-2">
                  03 / Duration
                </p>
                <p className="font-display text-xl sm:text-2xl lg:text-3xl text-white font-light tracking-tight">
                  2–6 Weeks
                </p>
                <p className="text-xs text-cinema-muted font-sans mt-1">
                  Intensive studio production
                </p>
              </div>

              <div className="py-6 sm:py-7 px-4 sm:px-6 lg:px-8 last:pr-0">
                <p className="text-[0.65rem] sm:text-xs font-mono uppercase tracking-[0.2em] text-cinema-gold font-semibold mb-2">
                  04 / Outcome
                </p>
                <p className="font-display text-xl sm:text-2xl lg:text-3xl text-white font-light tracking-tight">
                  Portfolio Asset
                </p>
                <p className="text-xs text-cinema-muted font-sans mt-1">
                  Verified work ready to show
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </header>

      {loading ? (
        <div className="py-20 flex justify-center"><LoadingSpinner /></div>
      ) : (
        <>
          {/* ── Featured Project (Primary Commercial Proposition) ── */}
          {featured && (
            <section className="py-12 sm:py-16 border-b border-white/5">
              <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
                <Reveal>
                  <p className="text-xs uppercase tracking-[0.2em] text-cinema-gold font-sans font-semibold mb-2">
                    Now Open &middot; Featured Project
                  </p>
                  <h2 className="font-display text-xl sm:text-3xl md:text-4xl text-white font-light tracking-tight">
                    {featured.title}
                  </h2>
                </Reveal>

                <Reveal delay={80} className="mt-6 sm:mt-8 grid lg:grid-cols-5 gap-8 lg:gap-12">
                  {/* Left: Description + Workflow */}
                  <div className="lg:col-span-3 space-y-6">
                    <p className="text-sm sm:text-base text-cinema-soft/85 leading-relaxed font-sans whitespace-pre-line">
                      {featured.short_description || featured.description}
                    </p>

                    {/* Workflow Stages — Numbered Editorial Sequence */}
                    {featured.stages && featured.stages.length > 0 && (
                      <div className="mt-8 pt-8 border-t border-white/10">
                        <div className="flex items-baseline justify-between mb-4">
                          <p className="text-xs font-mono uppercase tracking-[0.2em] text-cinema-gold font-semibold">
                            Project Production Sequence
                          </p>
                          <span className="text-[0.7rem] font-mono uppercase tracking-wider text-cinema-muted">
                            {featured.stages.length} Stages
                          </span>
                        </div>
                        <div className="divide-y divide-white/5 border-t border-b border-white/5">
                          {featured.stages.map((stage, i) => (
                            <div key={i} className="py-3 flex items-baseline gap-4">
                              <span className="font-mono text-xs text-cinema-gold/80 font-medium w-6 shrink-0">
                                {String(stage.order || i + 1).padStart(2, '0')}
                              </span>
                              <div className="min-w-0 flex-1 sm:grid sm:grid-cols-12 sm:gap-4 items-baseline">
                                <p className="sm:col-span-5 text-sm text-white font-sans font-medium">
                                  {stage.title}
                                </p>
                                {stage.description && (
                                  <p className="sm:col-span-7 text-xs text-cinema-muted font-sans mt-0.5 sm:mt-0 leading-relaxed">
                                    {stage.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: Open Editorial Specification Strip + CTA */}
                  <div className="lg:col-span-2 space-y-6">
                    <div className="border-t border-b border-white/10 divide-y divide-white/5">
                      <div className="py-3.5 flex justify-between items-center">
                        <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Discipline</span>
                        <span className="text-sm text-white font-sans font-medium">{featured.discipline}</span>
                      </div>
                      <div className="py-3.5 flex justify-between items-center">
                        <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Level</span>
                        <span className="text-sm text-white font-sans font-medium">{featured.level}</span>
                      </div>
                      <div className="py-3.5 flex justify-between items-center">
                        <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Duration</span>
                        <span className="text-sm text-white font-sans">{featured.duration || `${featured.duration_weeks} weeks`}</span>
                      </div>
                      <div className="py-3.5 flex justify-between items-center">
                        <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Cohort Size</span>
                        <span className="text-sm text-white font-sans">Max {featured.capacity}</span>
                      </div>
                      <div className="py-3.5 flex justify-between items-center">
                        <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Availability</span>
                        <span className={`text-sm font-sans font-medium ${featured.spots_remaining > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {featured.spots_remaining > 0 ? `${featured.spots_remaining} spots remaining` : 'Full'}
                        </span>
                      </div>
                      <div className="py-3.5 flex justify-between items-center">
                        <span className="text-xs uppercase tracking-wider text-cinema-muted font-sans">Project Lab Fee</span>
                        <span className="text-sm font-mono font-medium text-cinema-gold">
                          {featured.mentorship_fee ? `UGX ${Number(featured.mentorship_fee).toLocaleString()}` : 'Included'}
                        </span>
                      </div>
                    </div>

                    {/* Unboxed Editorial Final Deliverable Callout */}
                    {featured.final_deliverable && (
                      <div className="border-l-2 border-cinema-gold pl-4 py-1.5 space-y-1">
                        <p className="text-xs font-mono uppercase tracking-[0.2em] text-cinema-gold font-semibold">
                          Final Deliverable
                        </p>
                        <p className="text-sm text-white font-sans leading-relaxed">
                          {featured.final_deliverable}
                        </p>
                      </div>
                    )}

                    <Link
                      to={`/project-lab/${featured.slug}`}
                      className="cinema-btn-primary rounded-none !w-full !py-4 !text-xs uppercase tracking-[0.18em] font-bold flex items-center justify-center gap-2 min-h-[48px]"
                    >
                      <span>View Project & Apply</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </Reveal>
              </div>
            </section>
          )}

          {/* ── Other Projects (Restrained Catalogue) ── */}
          {projects.length > 0 && (
            <section className="py-12 sm:py-16">
              <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
                <Reveal>
                  <p className="text-xs uppercase tracking-[0.2em] text-cinema-muted font-sans font-semibold mb-2">
                    Project Catalogue
                  </p>
                  <h2 className="font-display text-lg sm:text-2xl text-white font-light tracking-tight">
                    Upcoming Projects
                  </h2>
                  <p className="mt-2 text-xs text-cinema-muted font-sans max-w-lg">
                    These projects are in development. Follow SMA for announcements when new project cohorts open.
                  </p>
                </Reveal>

                <div className="mt-6 sm:mt-8 divide-y divide-white/5 border-t border-b border-white/5">
                  {projects.map((project, i) => {
                    const Icon = DISCIPLINE_ICONS[project.discipline] || Target
                    return (
                      <Reveal key={project.id} delay={i * 40}>
                        <div className="py-4 sm:py-5 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center border border-white/10 text-cinema-muted shrink-0 rounded-none">
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm sm:text-base text-white font-sans font-medium truncate">{project.title}</p>
                              <p className="text-xs text-cinema-muted font-sans">{project.discipline} · {project.level}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="hidden sm:block text-xs text-cinema-muted font-sans uppercase tracking-wider">
                              {project.status === 'open' || project.status === 'filling'
                                ? 'Open'
                                : project.status === 'completed' ? 'Completed' : 'Coming Soon'}
                            </span>
                            {(project.status === 'open' || project.status === 'filling') && (
                              <Link
                                to={`/project-lab/${project.slug}`}
                                className="text-cinema-gold hover:text-white text-xs font-sans font-semibold uppercase tracking-wider transition-colors flex items-center gap-1 rounded-none"
                              >
                                View <ArrowRight className="w-3 h-3" />
                              </Link>
                            )}
                          </div>
                        </div>
                      </Reveal>
                    )
                  })}
                </div>
              </div>
            </section>
          )}

          {/* ── Private Project Lab CTA ── */}
          <section className="py-12 sm:py-16 border-t border-white/5">
            <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
              <Reveal className="max-w-2xl">
                <p className="text-xs uppercase tracking-[0.2em] text-cinema-gold font-sans font-semibold mb-2">
                  Private Project Lab
                </p>
                <h2 className="font-display text-lg sm:text-2xl text-white font-light tracking-tight">
                  Bring Your Project
                </h2>
                <p className="mt-3 text-sm text-cinema-soft/80 font-sans leading-relaxed">
                  Have a specific project, professional client brief, or desired outcome?
                  Work one-on-one with an SMA mentor through a custom project scope,
                  timeline, and deliverable.
                </p>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center gap-2 text-cinema-gold hover:text-white text-xs font-sans font-semibold uppercase tracking-wider transition-colors rounded-none"
                >
                  <span>Get in Touch</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </Reveal>
            </div>
          </section>
        </>
      )}

      <CinematicFooter />
    </div>
  )
}
